import { Button, LoadingOverlay, Modal, rem, Text } from "@mantine/core";
import React from "react";
import { notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";
import { Position } from "../models";
import { useQuery } from "react-query";
import axios from "axios";
import { FaChessBoard } from "react-icons/fa6";

interface Props {
    opened: boolean;
    close: () => void;
    position: Position;
    onDeletePosition: (position: Position) => void;
    numPositionsOfColor: number;
}

const getNumAttemptsForPosition = async (position: Position) => {
    const res = await axios.get(`${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/ot_num_attempts_for_position`, {
        withCredentials: true,
        params: {
            positionId: position.id,
        },
    });
    return res.data.attempts;
};

const DeletePositionModal: React.FC<Props> = ({ opened, close, position, onDeletePosition, numPositionsOfColor }) => {
    const { data: numAttempts, isLoading } = useQuery(["numAttemptsForPosition"], () =>
        getNumAttemptsForPosition(position),
    );

    const isLastPosition = numPositionsOfColor <= 1;

    if (isLoading) {
        return <LoadingOverlay />;
    }

    return (
        <Modal
            opened={opened}
            onClose={close}
            title={
                <Text fz="h3" fw="bold">
                    {isLastPosition ? "Cannot delete" : `Delete ${position.name}?`}
                </Text>
            }
            centered
            padding={20}
            size="lg"
        >
            <div className="">
                {isLastPosition ? (
                    <Text c="red">Cannot delete. You need to have at least 1 position.</Text>
                ) : (
                    <>
                        <Text>
                            Are you sure you want delete this position? This action will delete all games played on this
                            position.
                        </Text>

                        <div className="ml-8 flex items-center mt-8">
                            <FaChessBoard size={30} />
                            <Text mx="0.5rem" fz="h2" fw="bolder" span>
                                {numAttempts} games
                            </Text>{" "}
                            <Text span fz="lg" fw="normal">
                                will be deleted
                            </Text>
                        </div>
                    </>
                )}
                <div className="flex justify-center items-center space-x-4">
                    <Button
                        className="mt-6"
                        color="white"
                        variant="outline"
                        h="3rem"
                        onClick={() => {
                            close();
                        }}
                        fullWidth
                    >
                        <span className="text-lg">Cancel</span>
                    </Button>
                    {!isLastPosition && (
                        <Button
                            className="mt-6"
                            color="red"
                            h="3rem"
                            onClick={() => {
                                onDeletePosition(position);
                                notifications.show({
                                    title: `${position.name} has been deleted`,
                                    message: "",
                                    icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                                    color: "green",
                                });
                                close();
                            }}
                            fullWidth
                        >
                            <span className="text-lg">Delete position</span>
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default DeletePositionModal;
